import { N as Notyf } from './notyf-DIw0RvBj.js';
import { C as Cart } from './cart-BOtkBlOs.js';
import { P as Pubsub } from './pubsub-DKqIEqaC.js';

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.productId = this.dataset.productId;

    this.init();
    this.sendAnalytics();

    Pubsub.subscribe(`variant:change:${this.productId}`, () => this.toggleLoading(true));

    // Handle Safari bfcache restoration
    this.handleBfcacheRestoration();

    this.unsubscribeVariantChange = Pubsub.subscribe(
      `variant:changed:${this.productId}`,
      ({ html }) => {
        const productForm = html.querySelector('product-form');
        if (productForm) this.innerHTML = productForm.innerHTML;
        this.init();
        this.sendAnalytics();
      }
    );
  }

  disconnectedCallback() {
    this.unsubscribeVariantChange();
    this.unsubscribeQuantityChange();

    if (this.pageShowHandler) window.removeEventListener('pageshow', this.pageShowHandler);
  }

  init() {
    this.form = this.querySelector('form');
    this.quantityInput = this.form.querySelector('input[name="quantity"]');
    this.variantIdInput = this.form.querySelector('input[name="id"]');
    this.form.addEventListener('submit', (event) => this.onFormSubmit(event));

    // Get all submit buttons that are part of the form
    this.submitButtons = document.querySelectorAll(
      `a.button--submit, button[type="submit"], button[form="${this.form.getAttribute('id')}"]`
    );

    this.unsubscribeQuantityChange = Pubsub.subscribe('quantity:change', ({ quantity }) => {
      this.quantityInput.value = quantity;
    });
  }

  /**
   * Handle form submission
   * @param {Event} event
   */
  async onFormSubmit(event) {
    event.preventDefault();

    if (!this.validateVariantSelection()) return;

    this.toggleLoading(true);

    try {
      await Cart.add({ form: this.form });
    } catch (error) {
      console.error("Couldn't add to cart", error);
      Notyf.error(window.toast_translations.product.add_error);
    } finally {
      this.toggleLoading(false);
    }
  }

  /**
   * Validate that all required variants are selected.
   * @returns {boolean} - True if all variants are selected; false otherwise.
   */
  validateVariantSelection() {
    const unselected = [...this.querySelectorAll('fieldset.option')].filter(
      (fieldset) => !fieldset.querySelector('input[type="radio"]:checked')
    );

    if (unselected.length > 0) {
      unselected.forEach((fieldset) => {
        const legend = fieldset.querySelector('legend');
        legend?.classList.add('text-error');
      });

      this.variantChangeListener();

      return false;
    }

    return true;
  }

  /**
   * Adds a variant change listener to the form.
   * This method is called when variant validation fails to add a listener
   * that will remove error styling when a variant is selected.
   * @returns {void}
   */
  variantChangeListener() {
    if (this._hasVariantListener) return;
    this._hasVariantListener = true;

    // Listen for any 'change' event on <input type="radio"> that lives inside .option
    this.addEventListener(
      'change',
      (event) => {
        const input = event.target;
        if (input.matches('fieldset.option input[type="radio"]') && input.checked) {
          const fieldset = input.closest('fieldset.option');
          const legend = fieldset?.querySelector('legend');
          legend?.classList.remove('text-error');
        }
      },
      false
    );
  }

  /**
   * Toggle loading state on submit buttons
   * @param {boolean} loading
   */
  toggleLoading(loading) {
    this.submitButtons.forEach((button) => button.classList.toggle('loading', loading));
  }

  /**
   * Handle Safari bfcache restoration by listening to pageshow events
   */
  handleBfcacheRestoration() {
    this.clearErrorStates();

    const handlePageShow = (event) => {
      if (event.persisted) this.clearErrorStates();
    };

    window.addEventListener('pageshow', handlePageShow);
    this.pageShowHandler = handlePageShow;
  }

  /**
   * Clear any existing error states from variant fieldsets
   */
  clearErrorStates() {
    const allVariantFieldsets = Array.from(this.querySelectorAll('fieldset.option'));
    allVariantFieldsets.forEach((fieldset) => {
      const legend = fieldset.querySelector('legend');
      legend?.classList.remove('text-error');
    });
  }

  sendAnalytics() {
    const productJson = JSON.parse(this.querySelector('[data-product-json]').textContent);
    Shopify.analytics.publish('karl:product_variants', {
      productVariants: [productJson],
    });
  }
}

customElements.define('product-form', ProductForm);
